import { CircleAlert, CircleSlash, PenLine, ShieldCheck } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

import type { HealthPresentation } from './health-presentation'

/**
 * The health cell.
 *
 * A scored group shows its number; an unscored one shows words, in a tone that is neither the
 * green of a pass nor the red of a failure, with an icon that reads as "no reading". The two
 * cannot be mistaken for each other at a glance down a column, which is the only way this cell is
 * ever read.
 */
export function HealthIndicator({
  health,
  className,
}: {
  health: HealthPresentation
  className?: string
}) {
  const Icon = health.icon

  return (
    <Badge
      tone={health.tone}
      // The dashed edge marks "no reading" as a shape as well as a colour, so it survives
      // greyscale printing and a colour-blind reader — the same reason a badge never carries its
      // meaning in colour alone anywhere else in this application.
      className={cn(!health.isScored && 'border-dashed', className)}
      title={`${health.outcomeLabel} — ${health.explanation}`}
    >
      <Icon className="size-3" aria-hidden />
      <span className={cn(health.isScored && 'numeric tabular-nums')}>{health.label}</span>
    </Badge>
  )
}

/**
 * Whether the group qualifies for another round.
 *
 * Three answers, not two. "No" is a decision taken against a score; "not yet" is the absence of a
 * score to take one against, and a branch manager scanning for candidates has to be able to tell
 * those apart — the second one is fixed by holding meetings, the first is not.
 */
export function RoundEligibility({ health }: { health: HealthPresentation }) {
  if (health.qualifies) {
    // Qualification by attestation is a person's countersigned judgement, not a measurement, and
    // must not wear the same green as a group the arithmetic passed.
    if (!health.isScored) {
      return (
        <Badge tone="warning" className="border-dashed" title={health.explanation}>
          <PenLine className="size-3" aria-hidden />
          Qualifies · attested
        </Badge>
      )
    }

    return (
      <Badge tone="success">
        <ShieldCheck className="size-3" aria-hidden />
        Qualifies
      </Badge>
    )
  }

  if (health.isScored) {
    return (
      <Badge tone="danger">
        <CircleAlert className="size-3" aria-hidden />
        Does not qualify
      </Badge>
    )
  }

  return (
    <Badge tone="neutral" className="border-dashed" title={health.explanation}>
      <CircleSlash className="size-3" aria-hidden />
      Not yet — no score
    </Badge>
  )
}
